/**
 * TopNav - the primary navigation bar for the SignalSentry client app.
 *
 * Two layouts depending on the authenticated user's actor_type:
 *
 *   internal_staff: [Logo] ... [FLEET] [SENTRIES] [ALERTS] [NOW] ... [theme] [user]
 *   client_user:    [Logo] ... [NOW] [TIMELINE(disabled)] [INSIGHTS(disabled)] [SITES(disabled)] ... [theme] [user]
 *
 * Phase 12 Plan 12-07 (2026-05-19): the staff SITES nav item is renamed to
 * SENTRIES (display-only). The route target stays /admin/sites for back-
 * compat; the new /admin/sentries alias also resolves to the same page.
 * The data-testid attribute (`nav-sentries`) shifts with the label, since
 * it is generated from `item.label.toLowerCase()`. The client-side disabled
 * SITES placeholder is unchanged since the existing top-nav test exercises
 * `data-testid="nav-sites"` against the client branch.
 *
 * Per ADR-006 amendment 2026-04-27 staff JWTs register both admin and client
 * routes. The TopNav matches the route surface: staff get clickable links to
 * each admin page plus NOW for client-view drill-in; clients get only NOW
 * with the Phase 4/5 placeholders.
 *
 * Height: 56px per design spec section 2.
 *
 * Phase 4 amendment 2026-04-27:
 *   - Nav items cluster at the horizontal center of the bar (was flush-left
 *     next to the logo). User feedback: "want the header/navbar to be centered."
 *   - SOC theme toggle (sun/moon) inserted left of the user menu. Calls
 *     useTheme().toggleMode() to swap data-theme on <html>; localStorage
 *     persists the choice.
 *
 * Disabled nav items are rendered as non-interactive spans with aria-disabled="true"
 * and wrapped in Radix Tooltip showing the planned phase context.
 *
 * No em dashes in tooltip copy; uses parentheses and single hyphens only.
 *
 * Plan 03-04: Log out menu item wired to useAuth().logout() + navigate to /login.
 */
import { useCallback, useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@radix-ui/react-tooltip';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@radix-ui/react-dropdown-menu';
import { CaretDown, List, Moon, Sun, User } from '@phosphor-icons/react';
import { useAuth } from '@/features/auth/useAuth';
import { useTheme } from '@/design/ThemeProvider';
import { MobileDrawer } from '@/components/MobileDrawer';
import {
  navBar,
  navLogo,
  navMascot,
  navItems,
  navItemActive,
  navItemInactive,
  navItemDisabled,
  navMobileMenu,
  navRight,
  themeToggleButton,
  tooltipContent,
  dropdownMenuContent,
  dropdownMenuItem,
  userMenuTrigger,
} from './TopNav.css';

/** Disabled nav item definition with tooltip copy. */
interface DisabledNavItem {
  label: string;
  tooltip: string;
}

/** Active staff nav items (each is a clickable NavLink to an admin route). */
const STAFF_NAV_ITEMS: { label: string; to: string }[] = [
  { label: 'FLEET', to: '/admin/fleet' },
  // Phase 12 Plan 12-07 (2026-05-19): "SITES" -> "SENTRIES" display rename.
  // Route still points at /admin/sites for back-compat (the alias
  // /admin/sentries also resolves to the same page).
  { label: 'SENTRIES', to: '/admin/sentries' },
  { label: 'ALERTS', to: '/admin/alerts' },
  // Phase 10 follow-up 2026-05-01: replay-discovery surface. Sits next
  // to ALERTS in the nav so the visual relationship between an alert
  // and its replay stays obvious.
  //
  // Phase 19 / CONS-04 (2026-05-22): label renamed REPLAYS -> RECORDINGS
  // and route target moved from /admin/replays to /admin/recordings.
  // The legacy /admin/replays URL stays as a Navigate redirect for ~1
  // release (registered in build-routes.tsx) so externally-shared
  // bookmarks still land users on the new URL. The nav data-testid is
  // derived from item.label.toLowerCase() so the testid flips from
  // nav-replays to nav-recordings alongside the label.
  { label: 'RECORDINGS', to: '/admin/recordings' },
  // Mobile Uploads review surface (re-activated 2026-05-29). RECORDINGS now
  // shows only ENRICHED mobile sessions (+ alert replays); UPLOADS lists ALL
  // pushed mobile recordings + their pushed/pulled/enriched status, so raw
  // uploads excluded from RECORDINGS stay reviewable. Route stays
  // /admin/mobile-sessions (the page's own internal links target it).
  { label: 'UPLOADS', to: '/admin/mobile-sessions' },
  // 2026-06-10 (doc D issue 1): /admin/locations and /admin/orgs were
  // registered routes with NO nav entry anywhere - operators had to type
  // the URLs. Surfaced here (and mirrored in MobileDrawer) so the Location
  // CRUD page (which feeds sentry coords + satellite maps) and the org
  // admin surface are reachable. Order: management surfaces cluster after
  // the data surfaces, NOW stays last as the client-view drill-in.
  { label: 'LOCATIONS', to: '/admin/locations' },
  { label: 'ORGS', to: '/admin/orgs' },
  { label: 'NOW', to: '/now' },
];

/** The three nav items that are disabled for client_user in Phase 3. */
const CLIENT_DISABLED_NAV_ITEMS: DisabledNavItem[] = [
  { label: 'TIMELINE', tooltip: 'Timeline view (Phase 4)' },
  { label: 'INSIGHTS', tooltip: 'Insights analytics (Phase 5)' },
  { label: 'SITES', tooltip: 'Multi-site overview (Phase 4)' },
];

/**
 * TopNav component.
 * Renders the sticky top navigation bar with logo, role-aware nav items,
 * and user dropdown. Staff users see clickable admin links; clients see
 * NOW + disabled placeholders. The active route is highlighted via
 * NavLink + isActive.
 *
 * Plan 03-04: Logout wired to useAuth().logout() then navigate to /login.
 * If logout fails (network error), local tokens are cleared by logoutRequest's
 * try/catch, and AuthProvider's signalsentry:auth:expired handler updates the UI.
 */
export function TopNav() {
  const { user, logout } = useAuth();
  const { mode, toggleMode } = useTheme();
  const navigate = useNavigate();
  const isStaff = user?.actor_type === 'internal_staff' && !user?.impersonating_as;

  /**
   * Phase 20 MOB-02: drawer open state for the phone-width navigation
   * hamburger. Owned here so the parent TopNav controls both the
   * hamburger button's aria-expanded attribute AND the MobileDrawer's
   * `open` prop without needing a separate context.
   */
  const [drawerOpen, setDrawerOpen] = useState(false);

  /**
   * Handles the "Log out" dropdown item click.
   * Calls logout() to clear tokens + call /v1/auth/logout, then navigates
   * to /login. Navigation happens regardless of whether the server-side
   * revocation succeeds (local token clear is always performed by logoutRequest).
   */
  const handleLogout = useCallback(async () => {
    await logout();
    navigate('/login', { replace: true });
  }, [logout, navigate]);

  return (
    <TooltipProvider delayDuration={400}>
      <nav className={navBar} aria-label="Main navigation">
        {/*
         * Logo: mutemigit mascot mark + wordmark, always visible.
         * Clicks route to the role-aware home: /admin/fleet for staff,
         * /now for everyone else (matches buildRoutes default landing).
         */}
        <Link
          to={isStaff ? '/admin/fleet' : '/now'}
          className={navLogo}
          aria-label="SignalSentry, go home"
          data-testid="nav-logo-home"
        >
          <img
            src="/mutemigit.png"
            alt=""
            className={navMascot}
            width={48}
            height={48}
            draggable={false}
            aria-hidden="true"
          />
          <span>SignalSentry</span>
        </Link>

        {/* Nav items - role-aware (staff vs client) */}
        <ul className={navItems} role="list">
          {isStaff
            ? STAFF_NAV_ITEMS.map((item) => (
                <li key={item.label}>
                  <NavLink
                    to={item.to}
                    end={item.to === '/now'}
                    className={({ isActive }) =>
                      isActive ? navItemActive : navItemInactive
                    }
                    data-testid={`nav-${item.label.toLowerCase()}`}
                  >
                    {item.label}
                  </NavLink>
                </li>
              ))
            : (
              <>
                {/* Active: NOW - uses NavLink for aria-current="page" */}
                <li>
                  <NavLink
                    to="/now"
                    className={navItemActive}
                    aria-current="page"
                  >
                    NOW
                  </NavLink>
                </li>
                {/* Disabled items wrapped in tooltips (client-mode placeholders) */}
                {CLIENT_DISABLED_NAV_ITEMS.map((item) => (
                  <li key={item.label}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span
                          className={navItemDisabled}
                          aria-disabled="true"
                          role="link"
                          tabIndex={-1}
                          data-testid={`nav-${item.label.toLowerCase()}`}
                        >
                          {item.label}
                        </span>
                      </TooltipTrigger>
                      <TooltipContent
                        side="bottom"
                        sideOffset={8}
                        className={tooltipContent}
                      >
                        {item.tooltip}
                      </TooltipContent>
                    </Tooltip>
                  </li>
                ))}
              </>
            )}
        </ul>

        {/*
          Phase 20 MOB-02: hamburger button surfaced at phone widths
          (<=768px). The navMobileMenu CSS class is `display: none` at
          desktop and `display: flex` at both the legacy <=375px and the
          new <=768px clauses, so this button is invisible to desktop
          users + visible across the full phone-width activation range.
          Clicking opens the MobileDrawer rendered below; the drawer
          itself is Radix-Portal-rendered into document.body so the DOM
          placement here only matters for the button trigger.
        */}
        <button
          type="button"
          className={navMobileMenu}
          aria-label="Open navigation"
          aria-controls="mobile-nav-drawer"
          aria-expanded={drawerOpen}
          onClick={() => setDrawerOpen(true)}
          data-testid="nav-mobile-hamburger"
        >
          <List size={20} aria-hidden="true" />
        </button>

        {/* Right cluster: SOC theme toggle + user dropdown */}
        <div className={navRight}>
          {/* SOC theme toggle: sun in light mode (click to go dark), moon in dark mode */}
          {/* Phase 20 MOB-09: 32x32 is intentionally below the 44px tap-target
              floor because this is a tertiary preference control in the SOC
              monitoring station's compact toolbar pattern. The
              data-tap-target-ignore attribute is the explicit opt-out from
              the mobile-smoke audit per Plan 20-08. */}
          <button
            type="button"
            className={themeToggleButton}
            onClick={toggleMode}
            aria-label={`Switch to ${mode === 'dark' ? 'light' : 'dark'} mode`}
            data-testid="theme-toggle"
            data-tap-target-ignore
          >
            {mode === 'dark' ? (
              <Moon size={16} weight="fill" aria-hidden="true" />
            ) : (
              <Sun size={16} weight="fill" aria-hidden="true" />
            )}
          </button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              {/* Phase 20 MOB-09: the compact 30px-tall Account button is the
                  SOC toolbar pattern; the equivalent hamburger drawer (which
                  appears at <=768px) provides the primary mobile access path.
                  Annotated for the mobile-smoke audit per Plan 20-08. */}
              <button
                className={userMenuTrigger}
                aria-label="Open user menu"
                type="button"
                data-tap-target-ignore
              >
                <User size={16} aria-hidden="true" />
                <span>Account</span>
                <CaretDown size={12} aria-hidden="true" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              sideOffset={8}
              align="end"
              className={dropdownMenuContent}
            >
              {/* Profile - disabled in v1, wired in Plan 03-04 */}
              <DropdownMenuItem disabled className={dropdownMenuItem}>
                Profile
              </DropdownMenuItem>
              {/* Log out - clears tokens + calls /v1/auth/logout + navigates to /login */}
              <DropdownMenuItem
                onSelect={() => {
                  void handleLogout();
                }}
                className={dropdownMenuItem}
              >
                Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </nav>
      {/*
        Phase 20 MOB-02: the mobile-nav drawer. Open state owned by the
        TopNav so the hamburger button's aria-expanded stays in sync.
        Radix Dialog Portal-renders into document.body so this JSX
        position inside the nav is fine. The drawer is hidden by CSS
        media query at >=1025px (defense-in-depth even if open=true).
      */}
      <MobileDrawer open={drawerOpen} onOpenChange={setDrawerOpen} />
    </TooltipProvider>
  );
}
